import { useRef } from 'react';
import cx from 'classnames';
import PropTypes from 'prop-types';

import { SLATE_EDITOR_ACTIONS, SLATE_TEXT_FORMATS } from 'src/lib/slate';

import { TextStyleDropdown } from '../TextStyleDropdown';

import { ToolbarFormatActions, ToolbarShowMoreActions, ToolbarUndoRedoActions } from './components';
import { useHandleAction } from './useHandleAction';
import { useResponsiveFormatButtons } from './useResponsiveFormatButtons';
import { useStyles } from './useStyles';

export const FixedToolbar = ({
  className,
  classes,
  groupedActions,
  onEditorAction,
  onClearFormatting,
  defaultTextFormat,
  displayTextStyleDropdown,
  hideDefaultTextStyle,
  disabled,
}) => {
  const styles = useStyles({ classes });
  const rootRef = useRef();

  const { visibleGroups, hiddenActions, allActions } = useResponsiveFormatButtons({
    groupedActions,
    wrapperElement: rootRef.current,
    displayTextStyleDropdown,
  });

  const handleEditorAction = useHandleAction({
    onEditorAction,
    onClearFormatting,
    defaultTextFormat,
  });

  return (
    <div
      className={cx(styles.root, className)}
      ref={rootRef}
    >
      {displayTextStyleDropdown && (
        <TextStyleDropdown
          className={styles.group}
          editorActions={allActions}
          onAction={handleEditorAction}
          hideDefaultTextStyle={hideDefaultTextStyle}
          disabled={disabled}
        />
      )}

      {visibleGroups.map((groupActions, index) => (
        <ToolbarFormatActions
          className={styles.group}
          key={index}
          editorActions={groupActions}
          onAction={handleEditorAction}
          disabled={disabled}
        />
      ))}

      {hiddenActions.length > 0 && (
        <ToolbarShowMoreActions
          className={styles.group}
          editorActions={hiddenActions}
          onAction={handleEditorAction}
          disabled={disabled}
        />
      )}

      <ToolbarUndoRedoActions
        className={styles.group}
        editorActions={allActions}
        onAction={handleEditorAction}
        disabled={disabled}
      />
    </div>
  );
};

FixedToolbar.propTypes = {
  className: PropTypes.string,
  classes: PropTypes.object,
  groupedActions: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.oneOf(SLATE_EDITOR_ACTIONS))),
  onEditorAction: PropTypes.func,
  onClearFormatting: PropTypes.func,
  defaultTextFormat: PropTypes.oneOf(Object.values(SLATE_TEXT_FORMATS)),
  displayTextStyleDropdown: PropTypes.bool,
  hideDefaultTextStyle: PropTypes.bool,
  disabled: PropTypes.bool,
};

FixedToolbar.defaultProps = {
  className: '',
  classes: {},
  groupedActions: [],
  onEditorAction: null,
  onClearFormatting: null,
  defaultTextFormat: null,
  displayTextStyleDropdown: false,
  hideDefaultTextStyle: false,
  disabled: false,
};
